import React, { useState, useEffect, useContext } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import useApi from '../../hooks/useApi';
import haxios from '../../lib/haxios';
import { successToast, errorToast } from '../../lib/helpers';
import { format } from 'date-fns';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

function CommentsTable() {
  const { uinfo } = useContext(GlobalContext);
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  // Get customers for account name lookup
  const { data: customersData } = useApi({
    url: '/visiondashboard/customers/profiles',
    initial: []
  });

  // Fetch messages (comments)
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        const response = await haxios.get('/visiondashboard/messages');
        const messagesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        // Filter to only show messages from current user or all if admin
        const filtered = uinfo?.Status === 'admin' 
          ? messagesData 
          : messagesData.filter(msg => 
              msg.Op === uinfo?.Inits || 
              msg.staff_member === uinfo?.Inits ||
              msg.op === uinfo?.Inits
            );
        setMessages(filtered);
      } catch (error) {
        console.error('Error fetching messages:', error);
        errorToast('Failed to load comments');
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [uinfo]);

  // Filter messages
  useEffect(() => {
    let filtered = [...messages];

    // Search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(msg => {
        const accountId = msg.Acct || msg.account || msg.acct || '';
        const customer = customersData?.find(c => 
          c.KeyCode === accountId || 
          c.Acct === accountId ||
          c.account === accountId
        );
        const customerName = customer?.Name || customer?.name || customer?.CustomerName || '';
        const messageText = (msg.message || msg.note || '').toLowerCase();
        
        return accountId.toLowerCase().includes(lowerSearch) ||
               customerName.toLowerCase().includes(lowerSearch) ||
               messageText.includes(lowerSearch);
      });
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let startDate;
      
      switch (dateFilter) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered.filter(msg => {
          if (!msg.timestamp) return false;
          const msgDate = new Date(msg.timestamp);
          return msgDate >= startDate;
        });
      }
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
      const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
      return dateB - dateA;
    });

    setFilteredMessages(filtered);
  }, [messages, searchTerm, dateFilter, customersData]);

  const getCustomerName = (accountId) => {
    const customer = customersData?.find(c => 
      c.KeyCode === accountId || 
      c.Acct === accountId ||
      c.account === accountId
    );
    return customer?.Name || customer?.name || customer?.CustomerName || accountId;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500">Loading comments...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Comments</h2>
        <p className="text-sm text-gray-600 mt-1">
          {filteredMessages.length} comment{filteredMessages.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center space-x-4">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by account, customer name, or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">This Month</option>
        </select>
      </div>

      {/* Comments Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date/Time
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Account
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Staff Member
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comment
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredMessages.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                  No comments found.
                </td>
              </tr>
            ) : (
              filteredMessages.map((msg, idx) => {
                const accountId = msg.Acct || msg.account || msg.acct || 'N/A';
                const customerName = getCustomerName(accountId);
                
                return (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {msg.timestamp ? format(new Date(msg.timestamp), 'dd/MM/yyyy HH:mm') : 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                      {accountId}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {customerName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {msg.staff_member || msg.Op || msg.op || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="max-w-md whitespace-pre-wrap">
                        {msg.message || msg.note || 'No content'}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CommentsTable;

